import { type FC, useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { Alert, Breadcrumb, Input, Spin, Tooltip } from 'antd';
import { EditOutlined } from '@ant-design/icons';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';

import { useCustomerConfig, useTpoGenConfig } from '../../context/CustomerConfigContext';
import useComparisonReportDetail from '../hooks/queries/useComparisonReportDetail';
import useUniversePlans from '../hooks/queries/useUniversePlans';
import useScenarioKpiData from '../hooks/queries/useScenarioKpiData';
import useKpiBreakdownData from '../hooks/queries/useKpiBreakdownData';
import useSaveComparisonReportMutation from '../hooks/mutations/useSaveComparisonReportMutation';
import useUpdateComparisonReportMutation from '../hooks/mutations/useUpdateComparisonReportMutation';
import useDeleteComparisonReportMutation from '../hooks/mutations/useDeleteComparisonReportMutation';
import useScenarioComparisonState from '../hooks/ui/useScenarioComparisonState';
import {
    TPO_ADD_SCENARIO_MODAL,
    TPO_CHANGE_BASE_MODAL,
    TPO_SCENARIO_COMPARISON_BASE_MISSING_ALERT,
    TPO_SCENARIO_COMPARISON_BASE_MISSING_BTN,
    TPO_SCENARIO_COMPARISON_BREADCRUMB,
    TPO_SCENARIO_COMPARISON_EDIT_NAME_BTN,
    TPO_SCENARIO_COMPARISON_LOADING,
    TPO_SCENARIO_COMPARISON_PAGE,
    TPO_SCENARIO_COMPARISON_REPORT_NAME,
    TPO_SCENARIO_COMPARISON_REPORT_NAME_INPUT,
    TPO_SCENARIO_COMPARISON_SCENARIOS_MISSING_ALERT,
} from '../../config/testIds';
import { tpoGenKeys } from '../../utils/tpoGenQueryKeys';
import {
    TPOGEN_ANALYTICS_VIEWED,
    TPOGEN_BREADCRUMB_CLICKED,
    TPOGEN_COMPARE_KPI_COLUMNS_CHANGED,
    TPOGEN_COMPARE_PLANS_REPORT_DELETED,
    TPOGEN_COMPARE_PLANS_REPORT_RENAMED,
    TPOGEN_COMPARE_PLANS_REPORT_SAVED,
    TPOGEN_COMPARE_PLANS_REPORT_UPDATED,
    TPOGEN_PLAN_SELECTED_FOR_COMPARE,
} from 'utils/mixpanelEventNames';
import { trackTpoGenEvent, TPOGEN_PAGE_NAMES } from '../../utils/trackTpoGenEvent';
import ReportExportModal from '../../ReportExport/ReportExportModal';
import { useExportJobs } from '../../ReportExport/context/ExportJobsContext';
import { buildPlanComparisonExportPayload } from '../../ReportExport/utils/exportPayloadBuilders';
import type { ExportScope } from '../../ReportExport/types';
import ScenarioComparisonToolbar from './ScenarioComparisonToolbar';
import ScenarioComparisonGrid from './ScenarioComparisonGrid';
import KpiBreakdownTable from './components/KpiBreakdownTable';
import CreateNewReportModal from './components/CreateNewReportModal';
import PlanSelectionModal from './components/PlanSelectionModal';
import DeleteReportModal from './components/DeleteReportModal';
import type { ComparisonReportDetailResponse, CreateReportResult, ScenarioPlan } from '../types';

interface LocationState {
    baseScenario?: ScenarioPlan;
    dim1_labels?: string[];
    dim2_labels?: string[];
    reportId?: string;
    createNew?: boolean;
    fromSearch?: string;
}

/**
 * The comparison view has no time-period control, so the grid and its export
 * share this one value rather than each defaulting separately.
 */
const COMPARISON_TIME_PERIOD = 'full_year';

const PAGE_STYLE = { minHeight: 'calc(100vh - 130px)' } as const;
const REPORT_LOADER_STYLE = { minHeight: 400 } as const;
const EDIT_NAME_INPUT_STYLE = { width: 224, borderColor: '#44338A' } as const;
const EDIT_NAME_ICON_STYLE = { color: '#44338A' } as const;

const buildPlansIndexPath = (fromSearch?: string) => {
    const params = new URLSearchParams(fromSearch ?? '');
    params.set('tab', 'comparison-reports');
    return `/tpo-gen/plans-index?${params.toString()}`;
};

const buildReportPath = (reportId: string) => `/tpo-gen/scenario-comparison/${reportId}`;

const arraysEqualUnordered = (a: string[], b: string[]): boolean => {
    if (a.length !== b.length) return false;
    const sortedA = [...a].sort();
    const sortedB = [...b].sort();
    return sortedA.every((value, index) => value === sortedB[index]);
};

interface SavedReportSnapshot {
    name: string;
    basePlanId: string;
    comparisonPlanIds: string[];
    kpiList: string[];
    metricType: string;
}

const buildSavedSnapshot = (report: ComparisonReportDetailResponse): SavedReportSnapshot => ({
    name: report.name,
    basePlanId: report.base_plan.plan_id,
    comparisonPlanIds: (report.comparison_plans ?? []).map((p) => p.plan_id),
    kpiList: report.selection?.kpi_list ?? [],
    metricType: report.selection?.metric_type ?? 'abs',
});

const ScenarioComparison: FC = () => {
    const { state } = useLocation();
    const { reportId: reportIdParam } = useParams<{ reportId?: string }>();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const navState = (state as LocationState) ?? {};
    const tpoGenConfig = useTpoGenConfig();
    const { labels, scenarioComparisonConfig } = tpoGenConfig;
    const { maxComparisonSlots } = scenarioComparisonConfig;
    const { kpiCatalog } = useCustomerConfig();
    const { isExportPending } = useExportJobs();

    const isNewReport = reportIdParam === 'new' || (!!navState.createNew && !reportIdParam);
    const reportIdFromUrl = isNewReport ? null : reportIdParam ?? navState.reportId ?? null;
    const plansIndexPath = useMemo(() => buildPlansIndexPath(navState.fromSearch), [navState.fromSearch]);

    const { reportDetail, isPending: isReportLoading } = useComparisonReportDetail(reportIdFromUrl);

    const [highlightedKpiKey, setHighlightedKpiKey] = useState<string | null>(null);
    const [kpiLevel, setKpiLevel] = useState(() => scenarioComparisonConfig.kpiBreakdownLevels[0]?.key ?? '');
    const [kpiSearch, setKpiSearch] = useState('');
    const [tableSearch, setTableSearch] = useState('');
    const [isEditingName, setIsEditingName] = useState(false);
    const [editNameValue, setEditNameValue] = useState('');
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showExportModal, setShowExportModal] = useState(false);

    const hasTrackedPageViewRef = useRef(false);
    const lastSavedSnapshotRef = useRef<SavedReportSnapshot | null>(null);

    const handleHighlightChange = useCallback((kpiKey: string | null) => {
        setHighlightedKpiKey(kpiKey);
        setKpiSearch('');
    }, []);

    const {
        baseScenario,
        comparisonScenarios,
        comparisonPlanIds,
        selectedKpiKeys,
        setSelectedKpiKeys,
        metricType,
        handleToggleMetricType,
        showCreateReportModal,
        showChangeBaseModal,
        showAddScenarioModal,
        reportName,
        setReportName,
        reportId,
        reportFormData,
        allPlanIds,
        baseMissing,
        missingPlanIds,
        handleCloseCreateReportModal,
        handleOpenChangeBaseModal,
        handleCloseChangeBaseModal,
        handleOpenAddScenarioModal,
        handleCloseAddScenarioModal,
        handleCreateReport,
        handleReportSaved,
        handleChangeBase,
        handleApplyScenarios,
        handleRemoveScenario,
    } = useScenarioComparisonState({
        initialBaseScenario: navState.baseScenario,
        initialDim1Labels: navState.dim1_labels,
        initialDim2Labels: navState.dim2_labels,
        isNewReport,
        reportDetail,
    });

    const dim1Labels = reportFormData?.dim1_labels ?? [];
    const dim2Labels = reportFormData?.dim2_labels ?? [];

    const { plans: basePlans } = useUniversePlans({
        dim1Labels,
        dim2Labels,
        enabled: dim1Labels.length > 0 && dim2Labels.length > 0,
    });

    const excludeForComparison = useMemo(
        () => (baseScenario ? [baseScenario.planId, ...comparisonPlanIds] : comparisonPlanIds),
        [baseScenario, comparisonPlanIds],
    );

    const { plans: eligiblePlans } = useUniversePlans({
        dim1Labels,
        dim2Labels,
        exclude: excludeForComparison,
        enabled: dim1Labels.length > 0 && dim2Labels.length > 0 && !!baseScenario,
    });

    useEffect(() => {
        if (!reportDetail) return;
        lastSavedSnapshotRef.current = buildSavedSnapshot(reportDetail);
    }, [reportDetail]);

    useEffect(() => {
        if (hasTrackedPageViewRef.current) return;
        if (reportIdFromUrl && isReportLoading) return;
        hasTrackedPageViewRef.current = true;
        trackTpoGenEvent(TPOGEN_ANALYTICS_VIEWED, TPOGEN_PAGE_NAMES.SCENARIO_COMPARISON, {
            plan_id: baseScenario?.planId ?? reportDetail?.base_plan.plan_id ?? null,
            plan_count: reportDetail?.plans_count ?? (baseScenario ? eligiblePlans.length : basePlans.length),
        });
    }, [reportIdFromUrl, isReportLoading, baseScenario, reportDetail, eligiblePlans.length, basePlans.length]);

    const { data: kpiData, isFetching: isKpiFetching } = useScenarioKpiData({
        basePlanId: baseScenario?.planId ?? null,
        comparisonPlanIds,
        kpiList: selectedKpiKeys,
        metricType,
        timePeriod: COMPARISON_TIME_PERIOD,
        searchKey: tableSearch,
    });

    // With a search key the API returns only the matching KPIs, so the grid must follow the
    // response rather than the full dropdown selection.
    const matchedKpiKeys = useMemo(() => {
        if (!tableSearch.trim() || !kpiData?.data) return null;
        return kpiData.data.map((row) => row.kpi_key);
    }, [tableSearch, kpiData]);

    const kpiByPlanId = useMemo(() => {
        const map = new Map<string, Record<string, number | string | null>>();
        if (!kpiData?.data) return map;

        const basePlanId = kpiData.base_plan?.plan_id;
        if (basePlanId) {
            const baseValues: Record<string, number | null> = {};
            for (const row of kpiData.data) {
                baseValues[row.kpi_key] = row.base_value;
            }
            map.set(basePlanId, baseValues);
        }

        if (kpiData.comparison_plans) {
            for (const planRef of kpiData.comparison_plans) {
                const values: Record<string, number | null> = {};
                for (const row of kpiData.data) {
                    values[row.kpi_key] = row.comparisons[planRef.plan_id]?.value ?? null;
                }
                map.set(planRef.plan_id, values);
            }
        }
        return map;
    }, [kpiData]);

    const deltaByPlanId = useMemo(() => {
        const map = new Map<string, Record<string, number | null>>();
        if (!kpiData?.data || !kpiData.comparison_plans) return map;

        for (const planRef of kpiData.comparison_plans) {
            const deltas: Record<string, number | null> = {};
            for (const row of kpiData.data) {
                deltas[row.kpi_key] = row.comparisons[planRef.plan_id]?.delta ?? null;
            }
            map.set(planRef.plan_id, deltas);
        }
        return map;
    }, [kpiData]);

    const { mutate: saveReport, isPending: isSaving } = useSaveComparisonReportMutation({
        onSuccess: (saved) => {
            handleReportSaved(saved);
            queryClient.setQueryData(tpoGenKeys.comparisonReportDetail(saved.id), saved);
            lastSavedSnapshotRef.current = buildSavedSnapshot(saved);
            trackTpoGenEvent(TPOGEN_COMPARE_PLANS_REPORT_SAVED, TPOGEN_PAGE_NAMES.SCENARIO_COMPARISON, {
                report_id: saved.id,
                report_name: saved.name,
                change_type: 'create',
            });
            navigate(buildReportPath(saved.id), { replace: true, state: { fromSearch: navState.fromSearch } });
        },
        successMessage: labels.comparisonReport.notifications.saveSuccess,
        errorMessage: labels.comparisonReport.notifications.saveError,
    });
    const { mutate: updateReport, isPending: isUpdating } = useUpdateComparisonReportMutation({
        successMessage: labels.comparisonReport.notifications.updateSuccess,
        errorMessage: labels.comparisonReport.notifications.updateError,
    });
    const { mutate: deleteReport, isPending: isDeleting } = useDeleteComparisonReportMutation({
        onSuccess: () => {
            trackTpoGenEvent(TPOGEN_COMPARE_PLANS_REPORT_DELETED, TPOGEN_PAGE_NAMES.SCENARIO_COMPARISON, {
                report_id: reportId ?? '',
                report_name: reportName,
                change_type: 'delete',
            });
            setShowDeleteModal(false);
            navigate(plansIndexPath);
        },
        successMessage: labels.comparisonReport.notifications.deleteSuccess,
        errorMessage: labels.comparisonReport.notifications.deleteError,
    });

    const { rows: kpiRows, isFetching: isKpiLevelFetching } = useKpiBreakdownData({
        planIds: allPlanIds,
        basePlanId: baseScenario?.planId ?? null,
        kpiKey: highlightedKpiKey,
        groupBy: kpiLevel,
        metricType,
        search: kpiSearch,
    });

    const handleKpiSelectionChange = useCallback(
        (keys: string[]) => {
            setSelectedKpiKeys(keys);
            setHighlightedKpiKey((prev) => (prev != null && !keys.includes(prev) ? null : prev));
            trackTpoGenEvent(TPOGEN_COMPARE_KPI_COLUMNS_CHANGED, TPOGEN_PAGE_NAMES.SCENARIO_COMPARISON, {
                kpi_ids_count: keys.length,
                plan_count: allPlanIds.length,
            });
        },
        [setSelectedKpiKeys, allPlanIds],
    );

    const handleStartEditName = useCallback(() => {
        setEditNameValue(reportName);
        setIsEditingName(true);
    }, [reportName]);

    const handleConfirmEditName = useCallback(() => {
        const trimmed = editNameValue.trim();
        if (trimmed) setReportName(trimmed);
        setIsEditingName(false);
    }, [editNameValue, setReportName]);

    const handleEditNameKeyDown = useCallback(
        (e: KeyboardEvent<HTMLInputElement>) => {
            if (e.key === 'Enter') handleConfirmEditName();
            else if (e.key === 'Escape') setIsEditingName(false);
        },
        [handleConfirmEditName],
    );

    const handleCancelCreateReport = useCallback(() => {
        handleCloseCreateReportModal();
        navigate(plansIndexPath);
    }, [handleCloseCreateReportModal, navigate, plansIndexPath]);

    const handleSave = useCallback(() => {
        if (!baseScenario) return;

        const payload = {
            name: reportName,
            base_plan_id: baseScenario.planId,
            comparison_plan_ids: comparisonPlanIds,
            kpi_list: selectedKpiKeys,
            metric_type: metricType,
        };

        if (reportId) {
            const previous = lastSavedSnapshotRef.current;
            const onlyNameChanged =
                !!previous &&
                previous.name !== payload.name &&
                previous.basePlanId === payload.base_plan_id &&
                arraysEqualUnordered(previous.comparisonPlanIds, payload.comparison_plan_ids) &&
                arraysEqualUnordered(previous.kpiList, payload.kpi_list) &&
                previous.metricType === payload.metric_type;
            const changeType = onlyNameChanged ? 'rename' : 'update';

            updateReport(
                { reportId, payload },
                {
                    onSuccess: (data) => {
                        lastSavedSnapshotRef.current = buildSavedSnapshot(data);
                        trackTpoGenEvent(
                            changeType === 'rename'
                                ? TPOGEN_COMPARE_PLANS_REPORT_RENAMED
                                : TPOGEN_COMPARE_PLANS_REPORT_UPDATED,
                            TPOGEN_PAGE_NAMES.SCENARIO_COMPARISON,
                            {
                                report_id: data.id,
                                report_name: data.name,
                                change_type: changeType,
                            },
                        );
                    },
                },
            );
        } else {
            saveReport(payload);
        }
    }, [baseScenario, comparisonPlanIds, selectedKpiKeys, reportName, metricType, reportId, saveReport, updateReport]);

    const handleDelete = useCallback(() => {
        if (!reportId) {
            navigate(plansIndexPath);
            return;
        }
        setShowDeleteModal(true);
    }, [reportId, navigate, plansIndexPath]);

    const handleConfirmDelete = useCallback(() => {
        if (reportId) deleteReport(reportId);
    }, [reportId, deleteReport]);

    const handleRefresh = useCallback(() => {
        queryClient.invalidateQueries({ queryKey: tpoGenKeys.comparisonReports() });
        if (reportId) {
            queryClient.invalidateQueries({ queryKey: tpoGenKeys.comparisonReportDetail(reportId) });
        }
        queryClient.invalidateQueries({ queryKey: [...tpoGenKeys.all, 'plans-comparison-table'] });
        queryClient.invalidateQueries({ queryKey: [...tpoGenKeys.all, 'kpi-breakdown'] });
    }, [queryClient, reportId]);

    const changeBaseSubtitle = useMemo(() => {
        const dim1 = dim1Labels.join(', ') || labels.comparisonReport.dim1Label;
        const dim2 = dim2Labels.join(', ') || labels.comparisonReport.dim2Label;
        return labels.comparisonReport.changeBaseSubtitle(dim1, dim2);
    }, [dim1Labels, dim2Labels, labels]);

    const addScenarioSubtitle = useMemo(
        () => labels.comparisonReport.addScenarioSubtitle(maxComparisonSlots),
        [labels, maxComparisonSlots],
    );

    const handleChangeBaseConfirm = useCallback(
        (selectedPlanIds: string[]) => {
            const planId = selectedPlanIds[0];
            const plan = basePlans.find((p) => p.id === planId);
            if (plan) {
                const compareSetSizeAfter = comparisonScenarios.filter((s) => s.planId !== planId).length + 1;
                trackTpoGenEvent(TPOGEN_PLAN_SELECTED_FOR_COMPARE, TPOGEN_PAGE_NAMES.SCENARIO_COMPARISON, {
                    plan_id: planId,
                    compare_set_size: compareSetSizeAfter,
                });
                handleChangeBase(plan);
            }
        },
        [handleChangeBase, basePlans, comparisonScenarios],
    );

    const handleAddScenariosConfirm = useCallback(
        (selectedPlanIds: string[]) => {
            const selected = eligiblePlans.filter((p) => selectedPlanIds.includes(p.id));
            const compareSetSizeAfter = allPlanIds.length + selected.length;
            for (const plan of selected) {
                trackTpoGenEvent(TPOGEN_PLAN_SELECTED_FOR_COMPARE, TPOGEN_PAGE_NAMES.SCENARIO_COMPARISON, {
                    plan_id: plan.id,
                    compare_set_size: compareSetSizeAfter,
                });
            }
            handleApplyScenarios(selected);
        },
        [eligiblePlans, handleApplyScenarios, allPlanIds],
    );

    const handleCreateReportTracked = useCallback(
        (result: CreateReportResult) => {
            trackTpoGenEvent(TPOGEN_PLAN_SELECTED_FOR_COMPARE, TPOGEN_PAGE_NAMES.SCENARIO_COMPARISON, {
                plan_id: result.selectedPlan.planId,
                compare_set_size: 1,
            });
            handleCreateReport(result);
        },
        [handleCreateReport],
    );

    // Split from `canExport` so the modal is not torn down the moment the job it
    // just created flips the pending guard.
    const hasExportTarget = !!baseScenario && !baseMissing;
    const canExport =
        hasExportTarget && !isExportPending(baseScenario?.planId ?? '', 'plan_comparison');

    const handleExport = useCallback(() => setShowExportModal(true), []);
    const handleCloseExportModal = useCallback(() => setShowExportModal(false), []);

    const allKpiKeys = useMemo(() => kpiCatalog.map((kpi) => kpi.key), [kpiCatalog]);

    // `drilldowns` carries the KPI breakdown open on screen — one KPI at one
    // level. Closed, or under `all_data`, it goes out empty and the export is
    // the summary grid alone.
    const buildComparisonPayload = useCallback(
        (scope: ExportScope) =>
            buildPlanComparisonExportPayload({
                scope,
                basePlanId: baseScenario?.planId ?? '',
                comparisonPlanIds,
                kpiList: selectedKpiKeys,
                allKpiKeys,
                timePeriod: COMPARISON_TIME_PERIOD,
                reportName,
                highlightedKpiKey,
                kpiLevel,
                metricType,
                searchKey: tableSearch,
            }),
        [
            baseScenario,
            comparisonPlanIds,
            selectedKpiKeys,
            allKpiKeys,
            reportName,
            highlightedKpiKey,
            kpiLevel,
            metricType,
            tableSearch,
        ],
    );

    const highlightedKpi = useMemo(() => {
        if (!highlightedKpiKey) return undefined;
        if (matchedKpiKeys && !matchedKpiKeys.includes(highlightedKpiKey)) return undefined;
        return kpiCatalog.find((k) => k.key === highlightedKpiKey);
    }, [kpiCatalog, highlightedKpiKey, matchedKpiKeys]);

    return (
        <div data-testid={TPO_SCENARIO_COMPARISON_PAGE} className="flex flex-col" style={PAGE_STYLE}>
            {reportIdFromUrl && isReportLoading ? (
                <div
                    className="flex flex-1 items-center justify-center"
                    style={REPORT_LOADER_STYLE}
                    data-testid={TPO_SCENARIO_COMPARISON_LOADING}
                >
                    <Spin size="default" />
                </div>
            ) : (
                <>
                    <div className="flex items-center justify-between mb-4" data-testid={TPO_SCENARIO_COMPARISON_BREADCRUMB}>
                        <Breadcrumb>
                            <Breadcrumb.Item>
                                <Link
                                    to="/tpo-gen/plans-index"
                                    onClick={() =>
                                        trackTpoGenEvent(
                                            TPOGEN_BREADCRUMB_CLICKED,
                                            TPOGEN_PAGE_NAMES.SCENARIO_COMPARISON,
                                            {
                                                source_page: 'scenario_comparison',
                                                target_page: 'plans_list',
                                                object_type: 'plan',
                                            },
                                        )
                                    }
                                >
                                    {labels.common.breadcrumbRoot}
                                </Link>
                            </Breadcrumb.Item>
                            <Breadcrumb.Item>
                                <Link
                                    to={plansIndexPath}
                                    onClick={() =>
                                        trackTpoGenEvent(
                                            TPOGEN_BREADCRUMB_CLICKED,
                                            TPOGEN_PAGE_NAMES.SCENARIO_COMPARISON,
                                            {
                                                source_page: 'scenario_comparison',
                                                target_page: 'plans_list',
                                                object_type: 'plan',
                                            },
                                        )
                                    }
                                >
                                    {labels.comparisonReport.breadcrumb}
                                </Link>
                            </Breadcrumb.Item>
                            <Breadcrumb.Item>{labels.comparisonReport.scenarioComparison}</Breadcrumb.Item>
                        </Breadcrumb>
                    </div>

                    {baseMissing ? (
                        <Alert
                            type="warning"
                            showIcon
                            message={labels.comparisonReport.baseMissingMessage}
                            description={labels.comparisonReport.baseMissingDescription}
                            action={
                                <a
                                    onClick={handleOpenChangeBaseModal}
                                    className="cursor-pointer text-[#44338A] font-medium"
                                    data-testid={TPO_SCENARIO_COMPARISON_BASE_MISSING_BTN}
                                >
                                    {labels.comparisonReport.changeBaseScenario}
                                </a>
                            }
                            className="mb-4"
                            data-testid={TPO_SCENARIO_COMPARISON_BASE_MISSING_ALERT}
                        />
                    ) : null}

                    {missingPlanIds.length > 0 && !baseMissing ? (
                        <Alert
                            type="info"
                            showIcon
                            closable
                            message={labels.comparisonReport.scenariosMissingMessage(missingPlanIds.length)}
                            className="mb-4"
                            data-testid={TPO_SCENARIO_COMPARISON_SCENARIOS_MISSING_ALERT}
                        />
                    ) : null}

                    <div className="flex flex-col flex-1 bg-white border border-[#E4E4E7] rounded-lg px-[1.5rem] py-[1.75rem]">
                        <div className="flex items-center gap-2 mb-4">
                            {isEditingName ? (
                                <Input
                                    autoFocus
                                    size="small"
                                    value={editNameValue}
                                    onChange={(e) => setEditNameValue(e.target.value)}
                                    onBlur={handleConfirmEditName}
                                    onKeyDown={handleEditNameKeyDown}
                                    style={EDIT_NAME_INPUT_STYLE}
                                    data-testid={TPO_SCENARIO_COMPARISON_REPORT_NAME_INPUT}
                                />
                            ) : (
                            <Tooltip title={reportName}>
                                <div
                                    className="text-[20px] font-semibold text-[#18181B] truncate max-w-[600px]"
                                    data-testid={TPO_SCENARIO_COMPARISON_REPORT_NAME}
                                >
                                    {reportName}
                                </div>
                            </Tooltip>
                            )}
                            <EditOutlined
                                className="cursor-pointer"
                                style={EDIT_NAME_ICON_STYLE}
                                onClick={handleStartEditName}
                                data-testid={TPO_SCENARIO_COMPARISON_EDIT_NAME_BTN}
                            />
                        </div>

                        <ScenarioComparisonToolbar
                            selectedKpiKeys={selectedKpiKeys}
                            onKpiChange={handleKpiSelectionChange}
                            search={tableSearch}
                            onSearchChange={setTableSearch}
                            onChangeBaseScenario={handleOpenChangeBaseModal}
                            onAddScenario={handleOpenAddScenarioModal}
                            onSave={handleSave}
                            onDelete={handleDelete}
                            onRefresh={handleRefresh}
                            onExport={handleExport}
                            isSaving={isSaving || isUpdating}
                            hasBaseScenario={!!baseScenario}
                            canExport={canExport}
                            showPercent={metricType === 'percent'}
                            onShowPercentChange={handleToggleMetricType}
                        />

                        {!baseMissing ? (
                            <div className="mt-4">
                                <ScenarioComparisonGrid
                                    baseScenario={baseScenario}
                                    comparisonScenarios={comparisonScenarios}
                                    selectedKpiKeys={selectedKpiKeys}
                                    matchedKpiKeys={matchedKpiKeys}
                                    kpiByPlanId={kpiByPlanId}
                                    deltaByPlanId={deltaByPlanId}
                                    isKpiLoading={isKpiFetching}
                                    highlightedKpiKey={highlightedKpiKey}
                                    onHighlightChange={handleHighlightChange}
                                    onRemoveScenario={handleRemoveScenario}
                                    onAddScenarioClick={handleOpenAddScenarioModal}
                                    showPercent={metricType === 'percent'}
                                />
                            </div>
                        ) : null}

                        {highlightedKpi && !baseMissing ? (
                            <KpiBreakdownTable
                                kpi={highlightedKpi}
                                baseScenario={baseScenario}
                                comparisonScenarios={comparisonScenarios}
                                rows={kpiRows}
                                selectedKpiLevel={kpiLevel}
                                searchTerm={kpiSearch}
                                isFetching={isKpiLevelFetching}
                                showPercent={metricType === 'percent'}
                                onKpiLevelChange={setKpiLevel}
                                onSearchChange={setKpiSearch}
                            />
                        ) : null}
                    </div>

                    <CreateNewReportModal
                        open={showCreateReportModal}
                        onCancel={handleCancelCreateReport}
                        onCreate={handleCreateReportTracked}
                    />

                    <PlanSelectionModal
                        open={showChangeBaseModal}
                        title={labels.comparisonReport.changeBaseScenario}
                        subtitle={changeBaseSubtitle}
                        confirmLabel={labels.comparisonReport.createReportModal.create}
                        selectionMode="single"
                        plans={basePlans}
                        currentSingleSelection={baseScenario?.planId ?? null}
                        onConfirm={handleChangeBaseConfirm}
                        onCancel={handleCloseChangeBaseModal}
                        testId={TPO_CHANGE_BASE_MODAL}
                    />

                    <PlanSelectionModal
                        open={showAddScenarioModal}
                        title={labels.comparisonReport.addScenarioTitle}
                        subtitle={addScenarioSubtitle}
                        confirmLabel={labels.comparisonReport.add}
                        selectionMode="multiple"
                        plans={eligiblePlans}
                        maxSelections={maxComparisonSlots}
                        currentMultiSelections={comparisonPlanIds}
                        excludePlanIds={baseScenario ? [baseScenario.planId] : undefined}
                        onConfirm={handleAddScenariosConfirm}
                        onCancel={handleCloseAddScenarioModal}
                        testId={TPO_ADD_SCENARIO_MODAL}
                    />

                    {baseScenario && hasExportTarget ? (
                        <ReportExportModal
                            open={showExportModal}
                            onClose={handleCloseExportModal}
                            planId={baseScenario.planId}
                            reportKind="plan_comparison"
                            buildPayload={buildComparisonPayload}
                            planCount={allPlanIds.length}
                        />
                    ) : null}

                    <DeleteReportModal
                        open={showDeleteModal}
                        onClose={() => setShowDeleteModal(false)}
                        onConfirm={handleConfirmDelete}
                        reportName={reportName}
                        isPending={isDeleting}
                    />
                </>
            )}
        </div>
    );
};

ScenarioComparison.displayName = 'ScenarioComparison';
export default ScenarioComparison;
